import type {
  AskHelixMemoryArtifactDto,
  AskHelixQueryResponseDto,
  AskHelixSurfaceBindingDto,
  AskHelixSubjectBindingDto,
} from './ask-helix';

export type AskHelixAnswerModeDto =
  | 'decision_grade_answer'
  | 'exploratory_answer'
  | 'blocked_insufficient_truth';

export type AskHelixPublicBlockedReasonCodeDto =
  | 'answer_shape_gap'
  | 'blocked_named_gaps'
  // PR #5155 — a payload the tenant's cloud-egress policy or the actor's
  // permissions do not authorize. One closed code on REST and SSE alike.
  | 'cloud_egress_denied'
  // The API admits this namespace only after exact membership in its governed
  // plan-rejection vocabulary; the DTO never derives it from arbitrary text.
  | `manager_plan_${string}`;

export type AskHelixAnswerGradeDto = {
  mode: AskHelixAnswerModeDto;
  sufficientTruth: boolean;
};

export type AskHelixNextBestQuestionDto = {
  question: string;
  whyAdjacent: string;
  capabilityId?: string | null;
  toolName?: string | null;
  intentId?: string | null;
  intentClass?: string | null;
  answerType?: string | null;
  domain?: string | null;
};

export type AskHelixNextBestDto = {
  items: AskHelixNextBestQuestionDto[];
};

export type AskHelixBlockerDto = {
  headline: string;
  detail: string;
  whatIsNeeded?: string[] | null;
  suggestedQuestions?: string[] | null;
  nextBest?: AskHelixNextBestDto | null;
};

/**
 * RECOVERY IDENTITY KINDS (#4743 / #4755) — the closed, single-sourced taxonomy
 * of "what would unblock this turn", and the ONLY authority for a blocker's
 * `whatIsNeeded`.
 *
 * A blocked turn names its recovery identity; the composer derives the reader's
 * requirement from that identity. Explanatory answer prose never seeds the
 * requirement slot — narration is the answer body, not the instruction.
 *
 * The taxonomy is deliberately complete rather than convenient: an approval,
 * a permission, an ambiguous ask and a disconnected tool are four different
 * things to do, so they are four kinds. Collapsing them into
 * `capability_recovery` told every reader to "request a reconciled report",
 * which is wrong advice for three of the four (#4755 Codex review).
 *
 *  - `missing_evidence`       named governed evidence keys are absent
 *  - `capability_recovery`    the capability's governed readback must be re-run
 *  - `capability_connection`  the capability itself is not connected/registered
 *  - `approval_required`      an approval, opt-in or ledger binding is missing
 *  - `permission_required`    the actor/tenant may not read this in this scope
 *  - `clarification_required` the ask cannot be routed until it is narrowed
 *  - `tool_unavailable`       an approved tool/readback is not connected or failed
 */
export type AskHelixRecoveryPathKindDto =
  | 'missing_evidence'
  | 'capability_recovery'
  | 'capability_connection'
  | 'approval_required'
  | 'permission_required'
  | 'clarification_required'
  | 'tool_unavailable';

export const ASK_HELIX_RECOVERY_PATH_KINDS: readonly AskHelixRecoveryPathKindDto[] = [
  'missing_evidence',
  'capability_recovery',
  'capability_connection',
  'approval_required',
  'permission_required',
  'clarification_required',
  'tool_unavailable',
] as const;

export function isAskHelixRecoveryPathKind(
  value: unknown,
): value is AskHelixRecoveryPathKindDto {
  return (
    typeof value === 'string' &&
    (ASK_HELIX_RECOVERY_PATH_KINDS as readonly string[]).includes(value)
  );
}

export type AskHelixRelevantModuleSourceDto = 'server';

export type AskHelixRelevantModuleIdDto =
  | 'initiative_status_focus'
  | 'drift_focus'
  | 'org_sufficiency_assessment'
  | 'budget_allocation'
  | 'headcount_rollup'
  | 'capability_gap_summary'
  | 'promotion_readiness_summary';

export type AskHelixRelevantModuleDto = {
  id: AskHelixRelevantModuleIdDto | (string & {});
  title: string;
  source: AskHelixRelevantModuleSourceDto;
  summary?: string | null;
  sourceSurfaces?: AskHelixSurfaceBindingDto['surface'][] | null;
  status?: string | null;
  limit?: number | null;
  subjectKind?: AskHelixSubjectBindingDto['kind'] | null;
  subjectQuery?: string | null;
  reason?: string | null;
};

export type AskHelixRetrievedContextLayerDto =
  | 'artifact_metadata'
  | 'human_annotation'
  | 'code_enrichment'
  | 'institutional_knowledge'
  | 'memory'
  | 'runtime_context';

export type AskHelixRetrievedContextFreshnessDto = 'current' | 'stale' | 'unknown';

export type AskHelixRetrievedContextRefDto = {
  layer: AskHelixRetrievedContextLayerDto;
  title: string;
  freshness: AskHelixRetrievedContextFreshnessDto;
  selectionMode?: 'always_included' | 'ranked';
  reason?: string | null;
  asOf?: string | null;
};

export type AskHelixTraceStepKindDto =
  | 'intent'
  | 'retrieval'
  | 'tool_plan'
  | 'tool_execution'
  | 'answer_grade'
  | 'synthesis';

export type AskHelixTraceStepDto = {
  kind: AskHelixTraceStepKindDto;
  summary: string;
};

export type AskHelixAgentTraceDto = {
  steps: AskHelixTraceStepDto[];
};

/**
 * #5841 planner-prompt-observability follow-up — one bounded-retry planning
 * attempt (the manager allows at most two: `attempt` is 1 or 2). `reason` is
 * the server's own closed rejection reason, never provider prose.
 * `rejectedPlan` is the SAME already-redacted, provider-safe projection the
 * repair round itself sends back to the planner
 * (`RejectedManagerPlanProjection`, apps/api `manager-plan-rejection-projection.ts`)
 * — carried here as `Record<string, unknown>` rather than duplicating that
 * apps/api-internal shape in the shared DTO layer; it is already
 * size-bounded and reader-inert (droppedFieldCount/sourceRefStatus fields
 * only exist to prove the redaction happened, never a business figure).
 */
export type AskHelixManagerPlanningAttemptDto = {
  attempt: 1 | 2;
  reason: string;
  rejectedPlan: Record<string, unknown>;
};

export type AskHelixAgentResponseDto = AskHelixQueryResponseDto & {
  /** Present when an authorized retry returns a previously completed turn. */
  served?: {
    kind: 'replay';
    sourceUserTurnId: string;
    sourceAnswerTurnId: string;
  };
  answerMode: AskHelixAnswerModeDto;
  answerGrade: AskHelixAnswerGradeDto;
  blockedReasonCode: AskHelixPublicBlockedReasonCodeDto | null;
  blocker?: AskHelixBlockerDto | null;
  nextBest?: AskHelixNextBestDto | null;
  relevantModules: AskHelixRelevantModuleDto[];
  retrievedContextRefs: AskHelixRetrievedContextRefDto[];
  agentTrace: AskHelixAgentTraceDto;
  unknowns: string[];
  memoryProposals?: AskHelixMemoryArtifactDto[] | null;
  /**
   * #5841 — present ONLY on an `invalid_plan` turn: every bounded-retry
   * planning attempt (server-classified reason plus the redacted rejected
   * plan), so an `invalid_plan` response carries its own diagnosis instead
   * of requiring a log dig. Absent on every other turn outcome.
   */
  planningAttempts?: AskHelixManagerPlanningAttemptDto[] | null;
};

// The closed set of PUBLIC blockedReason values that mean a provider/synthesis
// outage (Helix-side infrastructure), not a governed data/capability gap
// (#4681 E3 review). Single-sourced here: the API maps its internal
// provider_unavailable to the synthesis name, and consumers (e.g. the
// rehearsal harness's dry-run) classify on this set — never on a substring
// predicate, which would sweep genuine governed states like
// approved_source_unavailable into the infra bucket.
export const ASK_HELIX_SYNTHESIS_SERVICE_UNAVAILABLE = 'synthesis_service_unavailable' as const;
export const ASK_HELIX_PROVIDER_OUTAGE_BLOCKED_REASONS = [
  ASK_HELIX_SYNTHESIS_SERVICE_UNAVAILABLE,
  'planner_contract_invalid',
] as const;
